import { FC } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { ItemDefinition } from '@app/api/QuestionnaireAdminStore';
import { ChangedItem, MeaningAnswers } from './questionnaireDefinitionEditing';

interface Props {
  open: boolean;
  version: number;
  changeReason: string;
  onChangeReason: (changeReason: string) => void;
  changedItems: ChangedItem[];
  answers: MeaningAnswers;
  onAnswer: (itemCode: string, changesMeaning: boolean) => void;
  onCancel: () => void;
  onPublish: () => void;
  publishing: boolean;
}

const describe = (item: ItemDefinition): string => {
  const choices = item.choices.map((choice) => `${choice.text} (${choice.scoreValue ?? '–'})`).join(', ');
  return [item.leadIn, item.text, choices && `Choices: ${choices}`].filter(Boolean).join(' · ');
};

/**
 * Asks why a new version of a questionnaire is being made, which is kept with the version for later analysis and
 * audits. Then, for each published item the admin changed, asks whether the change alters what the question means.
 * Research compares answers across item versions only when it doesn't, and a team in progress only gets the new
 * version when it doesn't.
 */
export const PublishVersionDialog: FC<Props> = ({
  open,
  version,
  changeReason,
  onChangeReason,
  changedItems,
  answers,
  onAnswer,
  onCancel,
  onPublish,
  publishing,
}) => {
  const allAnswered = changedItems.every((item) => item.code in answers);
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Publish version {version}</DialogTitle>
      <DialogContent>
        {/* The dialog drops its top padding under the title, which would clip the field's floating label. */}
        <Box sx={{ pt: 1 }}>
          <TextField
            label="Reason for change"
            placeholder="e.g. Reworded Q4 for clarity per clinical review"
            helperText="Kept with this version and shown in Version History, so later analysis and audits know why it changed."
            value={changeReason}
            onChange={(e) => onChangeReason(e.target.value)}
            required
            fullWidth
            multiline
            minRows={2}
            autoFocus
          />
        </Box>
        {changedItems.length > 0 && (
          <>
            <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
              Does this change what the question means?
            </Typography>
            <DialogContentText sx={{ mb: 2 }}>
              Each changed item gets a new version. If it means the same thing, teams already in progress see the new
              wording and their answers are compared with earlier ones. A change in meaning, such as a new time frame or
              different choices or scores, only reaches teams that haven&apos;t started: teams in progress finish on the
              version they started with, so their before and after answers still compare.
            </DialogContentText>
          </>
        )}
        {changedItems.map((item) => (
          <Box key={item.code} sx={{ mb: 3 }}>
            <Typography variant="subtitle1">{item.code}</Typography>
            <Typography variant="body2" color="text.secondary">
              Before: {describe(item.before)}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              After: {describe(item.after)}
            </Typography>
            <RadioGroup
              row
              value={item.code in answers ? String(answers[item.code]) : ''}
              onChange={(e) => onAnswer(item.code, e.target.value === 'true')}
            >
              <FormControlLabel value="false" control={<Radio />} label="No, same meaning (e.g. a typo fix)" />
              <FormControlLabel value="true" control={<Radio />} label="Yes, it means something different" />
            </RadioGroup>
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={publishing}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onPublish} disabled={!changeReason.trim() || !allAnswered || publishing}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
};
